import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  Loader2,
  ListTodo,
  CalendarDays,
  Target,
  TrendingUp,
  Sparkles,
  FileText,
} from 'lucide-react';
import { tasksApi, assetsApi, requirementsApi, roadmapApi } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { cn, getTaskStatusConfig, getPriorityConfig, formatDate } from '../lib/utils';
import { Progress } from '../components/ui/progress';

const taskStatuses = [
  { value: 'a_fazer', label: 'A Fazer' },
  { value: 'em_progresso', label: 'Em Progresso' },
  { value: 'bloqueada', label: 'Bloqueada' },
  { value: 'em_revisao', label: 'Em Revisão' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
];

const priorities = [
  { value: 'urgente', label: 'Urgente' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
];

const initialFormData = {
  title: '',
  description: '',
  requirement_id: '',
  asset_ids: [],
  priority: 'media',
  status: 'a_fazer',
  due_date: '',
  start_date: '',
  estimated_hours: '',
};

// Review checklists per module — audit-grade evidence for each review type
const REVIEW_CHECKLISTS = {
  policy: [
    { key: 'version_current', label: 'Confirmei que a versão atual está correta e visível para colaboradores' },
    { key: 'legal_updates', label: 'Comparei com regulação atual (NIS2, AI Act, RGPD) e legislação nacional' },
    { key: 'owner_approved', label: 'Obtive aprovação do dono/responsável da política' },
    { key: 'awareness', label: 'Comunicação/formação prevista para alterações materiais (se aplicável)' },
  ],
  supplier: [
    { key: 'dpa_valid', label: 'Contrato / DPA em vigor e assinado por ambas as partes' },
    { key: 'security_evidence', label: 'Revi evidências recentes (SOC 2, ISO 27001, questionário)' },
    { key: 'incidents_check', label: 'Verifiquei se houve incidentes ou breaches nos últimos 12 meses' },
    { key: 'criticality_ok', label: 'Confirmei a criticidade atribuída e escopo dos dados/serviços' },
  ],
  dpia: [
    { key: 'purpose_current', label: 'Finalidade do tratamento continua válida e proporcional' },
    { key: 'risks_updated', label: 'Riscos residuais reavaliados face a alterações do sistema/contexto' },
    { key: 'controls_verified', label: 'Verifiquei que as medidas técnicas e organizativas continuam eficazes' },
    { key: 'dpo_signoff', label: 'Aprovação registada do DPO' },
  ],
  audit: [
    { key: 'findings_closed', label: 'Findings da auditoria anterior fechados ou em plano de remediação' },
    { key: 'scope_current', label: 'Âmbito da próxima auditoria continua adequado' },
    { key: 'provider_ok', label: 'Provider/entidade auditora selecionada e budget aprovado' },
    { key: 'stakeholders_notified', label: 'Stakeholders internos notificados da janela da próxima auditoria' },
  ],
  bcm: [
    { key: 'rto_rpo_valid', label: 'RTO/RPO atuais continuam adequados face à criticidade do serviço' },
    { key: 'runbook_current', label: 'Runbook / plano DR atualizado nos últimos 12 meses' },
    { key: 'dependencies_ok', label: 'Dependências externas (ISPs, fornecedores) revalidadas' },
    { key: 'test_scheduled', label: 'Próximo teste tabletop/full-scale agendado e participantes identificados' },
  ],
};

const getReviewChecklistItems = (task) => {
  if (!task) return [];
  const module = (task.source || '').split('_')[0];
  return REVIEW_CHECKLISTS[module] || [];
};

export default function Tasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetIdFromUrl = searchParams.get('asset_id');
  
  const [tasks, setTasks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('all'); // "all" | "no_roadmap" | "ai_roadmap:RM-XXX"
  const [appliedRoadmaps, setAppliedRoadmaps] = useState([]);
  const [roadmapProgress, setRoadmapProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // Review-completion dialog state
  const [reviewCompleteTask, setReviewCompleteTask] = useState(null);
  const [reviewCycle, setReviewCycle] = useState(12);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewApprover, setReviewApprover] = useState('');
  const [reviewChecklist, setReviewChecklist] = useState({});
  const [completingReview, setCompletingReview] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter, priorityFilter, originFilter]);

  useEffect(() => {
    loadAppliedRoadmaps();
  }, []);

  useEffect(() => {
    if (originFilter.startsWith('ai_roadmap:')) {
      loadProgress(originFilter.split(':')[1]);
    } else {
      setRoadmapProgress(null);
    }
  }, [originFilter]);

  useEffect(() => {
    if (assetIdFromUrl) {
      setFormData(prev => ({ ...prev, asset_ids: [assetIdFromUrl] }));
      setShowForm(true);
    }
  }, [assetIdFromUrl]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (originFilter && originFilter !== 'all') params.source = originFilter;
      
      const [tasksRes, assetsRes, reqsRes] = await Promise.all([
        tasksApi.getAll(params),
        assetsApi.getAll({}),
        requirementsApi.getAll({}),
      ]);
      
      setTasks(tasksRes.data);
      setAssets(assetsRes.data);
      setRequirements(reqsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAppliedRoadmaps = async () => {
    try {
      const res = await roadmapApi.list();
      setAppliedRoadmaps((res.data || []).filter(r => r.applied_at));
    } catch (_) { /* silent */ }
  };

  const loadProgress = async (roadmapId) => {
    setLoadingProgress(true);
    try {
      const res = await roadmapApi.progress(roadmapId);
      setRoadmapProgress(res.data);
    } catch (e) {
      setRoadmapProgress(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }

    // Intercept: if this is a review task being completed, open the review-cycle dialog
    // instead of a plain update, so the linked policy/supplier/DPIA advances its next date.
    const isReviewTask = editingTask && (editingTask.source || '').endsWith('_review');
    const wasNotConcluded = editingTask && editingTask.status !== 'concluida';
    const nowConcluded = formData.status === 'concluida';
    if (isReviewTask && wasNotConcluded && nowConcluded) {
      // Default cycle based on module: policies use review_cycle_months in source metadata; fallback 12.
      setReviewCompleteTask(editingTask);
      setReviewCycle(12);
      setReviewNotes('');
      // Item 2 (iter 53): pre-fill approver with the current user's email
      setReviewApprover(user?.email || '');
      setReviewChecklist({});
      return;  // dialog will handle final save
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
      };

      if (editingTask) {
        await tasksApi.update(editingTask.task_id, payload);
        toast.success('Tarefa atualizada com sucesso');
      } else {
        await tasksApi.create(payload);
        toast.success('Tarefa criada com sucesso');
      }
      
      setShowForm(false);
      setEditingTask(null);
      setFormData(initialFormData);
      loadData();
    } catch (error) {
      toast.error(editingTask ? 'Erro ao atualizar tarefa' : 'Erro ao criar tarefa');
    } finally {
      setIsSaving(false);
    }
  };

  const submitReviewCompletion = async () => {
    if (!reviewCompleteTask) return;
    if (!reviewApprover.trim()) {
      toast.error('Indique quem aprovou a revisão (obrigatório para trail de auditoria).');
      return;
    }
    // Enforce all checklist items checked
    const checklistItems = getReviewChecklistItems(reviewCompleteTask);
    const missing = checklistItems.filter(item => !reviewChecklist[item.key]);
    if (missing.length > 0) {
      toast.error(`Complete os ${missing.length} item(s) da checklist antes de guardar.`);
      return;
    }
    setCompletingReview(true);
    try {
      const res = await api.post(`/tasks/${reviewCompleteTask.task_id}/complete-review`, {
        next_cycle_months: reviewCycle,
        notes: reviewNotes,
        approved_by: reviewApprover.trim(),
        checklist: reviewChecklist,
      });
      toast.success(`Revisão concluída · próxima em ${reviewCycle} meses (${res.data.next_review_date}).`);
      setReviewCompleteTask(null);
      setReviewApprover('');
      setReviewChecklist({});
      setReviewNotes('');
      setShowForm(false);
      setEditingTask(null);
      setFormData(initialFormData);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao concluir revisão.');
    } finally {
      setCompletingReview(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      requirement_id: task.requirement_id || '',
      asset_ids: task.asset_ids || [],
      priority: task.priority,
      status: task.status,
      due_date: task.due_date || '',
      start_date: task.start_date || '',
      estimated_hours: task.estimated_hours || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Tem a certeza que pretende eliminar esta tarefa?')) return;
    try {
      await tasksApi.delete(taskId);
      toast.success('Tarefa eliminada com sucesso');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar tarefa');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await tasksApi.update(task.task_id, { ...task, status: newStatus });
      toast.success('Estado atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  // Filter tasks by search
  const filteredTasks = tasks.filter(
    (task) =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.task_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by status
  const tasksByStatus = {
    a_fazer: filteredTasks.filter((t) => t.status === 'a_fazer' || t.status === 'pendente' || !t.status),
    em_progresso: filteredTasks.filter((t) => t.status === 'em_progresso'),
    em_revisao: filteredTasks.filter((t) => t.status === 'em_revisao'),
    concluida: filteredTasks.filter((t) => t.status === 'concluida'),
  };

  // Stats
  const stats = tasks.reduce(
    (acc, task) => {
      acc.total++;
      if (task.status === 'concluida') acc.completed++;
      if (task.status === 'bloqueada') acc.blocked++;
      if (task.due_date && new Date(task.due_date) < new Date() && task.status !== 'concluida' && task.status !== 'cancelada') {
        acc.overdue++;
      }
      return acc;
    },
    { total: 0, completed: 0, blocked: 0, overdue: 0 }
  );

  const headerContent = (
    <Button onClick={() => { setEditingTask(null); setFormData(initialFormData); setShowForm(true); }} data-testid="new-task-btn">
      <Plus className="h-4 w-4 mr-2" />
      Nova Tarefa
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Tarefas">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Tarefas" headerContent={headerContent}>
      <div className="space-y-6" data-testid="tasks-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-xs text-slate-500">Concluídas</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              <p className="text-xs text-slate-500">Em Atraso</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.blocked}</p>
              <p className="text-xs text-slate-500">Bloqueadas</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar tarefas..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-tasks-input"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {taskStatuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={priorityFilter}
                onValueChange={(value) => setPriorityFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={originFilter}
                onValueChange={setOriginFilter}
              >
                <SelectTrigger className="w-[220px]" data-testid="tasks-origin-filter">
                  <SelectValue placeholder="Origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as origens</SelectItem>
                  <SelectItem value="no_roadmap">Sem roadmap (manuais)</SelectItem>
                  {appliedRoadmaps.length > 0 && (
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                      Roadmaps aplicados
                    </div>
                  )}
                  {appliedRoadmaps.map((r) => (
                    <SelectItem key={r.roadmap_id} value={`ai_roadmap:${r.roadmap_id}`}>
                      <span className="font-mono text-xs mr-1">{r.roadmap_id}</span>
                      <span className="text-xs text-slate-500">· {r.total_tasks} tar.</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Roadmap progress banner */}
        {roadmapProgress && (
          <RoadmapProgressBanner
            data={roadmapProgress}
            loading={loadingProgress}
            onClear={() => setOriginFilter('all')}
          />
        )}

        {/* Kanban Board */}
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { key: 'a_fazer', title: 'A Fazer', color: 'border-slate-300' },
            { key: 'em_progresso', title: 'Em Progresso', color: 'border-blue-500' },
            { key: 'em_revisao', title: 'Em Revisão', color: 'border-amber-500' },
            { key: 'concluida', title: 'Concluída', color: 'border-green-500' },
          ].map((column) => (
            <Card key={column.key} className={cn('card-flat border-t-4', column.color)}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  {column.title}
                  <Badge variant="outline">{tasksByStatus[column.key]?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[500px] overflow-y-auto scrollbar-thin">
                {tasksByStatus[column.key]?.map((task) => {
                  const priorityConfig = getPriorityConfig(task.priority);
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'concluida';
                  
                  return (
                    <div
                      key={task.task_id}
                      className={cn(
                        'p-3 bg-white border rounded-md hover:shadow-sm transition-shadow cursor-pointer',
                        isOverdue && 'border-red-200 bg-red-50'
                      )}
                      onClick={() => handleEdit(task)}
                      data-testid={`task-${task.task_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{task.title}</p>
                          <p className="text-xs text-slate-500 font-mono">{task.task_id}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreHorizontal className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(task)}>
                              <Pencil className="h-3 w-3 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            {task.status === 'concluida' && (task.source || '').endsWith('_review') && (
                              <DropdownMenuItem
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const token = localStorage.getItem('auth_token');
                                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.task_id}/audit-trail.pdf`, {
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    if (!res.ok) throw new Error('http');
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `audit-trail-${task.task_id}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(url);
                                    toast.success('Trilho de auditoria gerado.');
                                  } catch { toast.error('Falha ao gerar PDF.'); }
                                }}
                                data-testid={`task-audit-trail-${task.task_id}`}
                              >
                                <FileText className="h-3 w-3 mr-2" />
                                Trilho de Auditoria (PDF)
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={(e) => { e.stopPropagation(); handleDelete(task.task_id); }}
                            >
                              <Trash2 className="h-3 w-3 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className={cn('text-[10px]', priorityConfig.color)}>
                          {priorityConfig.label}
                        </Badge>
                        {task.due_date && (
                          <span className={cn('text-[10px] flex items-center gap-1', isOverdue ? 'text-red-600' : 'text-slate-500')}>
                            <Calendar className="h-3 w-3" />
                            {formatDate(task.due_date)}
                          </span>
                        )}
                        {task.source && task.source.startsWith('ai_roadmap:') && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 cursor-pointer hover:bg-indigo-100"
                            onClick={(e) => { e.stopPropagation(); setOriginFilter(task.source); }}
                            data-testid={`task-roadmap-badge-${task.task_id}`}
                            title={`Filtrar por ${task.source}`}
                          >
                            <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                            W{task.roadmap_week || '?'} · {task.roadmap_id || task.source.split(':')[1]}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
                {tasksByStatus[column.key]?.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">Sem tarefas</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Task Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Atualize os detalhes da tarefa' : 'Crie uma nova tarefa de conformidade'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Implementar MFA no servidor X"
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição detalhada da tarefa..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taskStatuses.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Data Início</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Data Limite</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="requirement_id">Requisito NIS2 (opcional)</Label>
              <Select
                value={formData.requirement_id}
                onValueChange={(value) => setFormData({ ...formData, requirement_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger id="requirement_id">
                  <SelectValue placeholder="Selecionar requisito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {requirements.map((req) => (
                    <SelectItem key={req.requirement_id} value={req.requirement_id}>
                      {req.code} - {req.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving} data-testid="save-task-btn">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingTask ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review Completion Dialog — asks for next review cycle when completing a *_review task */}
      <Dialog open={!!reviewCompleteTask} onOpenChange={(o) => { if (!o) setReviewCompleteTask(null); }}>
        <DialogContent data-testid="review-complete-dialog">
          <DialogHeader>
            <DialogTitle>Concluir revisão</DialogTitle>
            <DialogDescription>
              Ao concluir esta tarefa, a próxima revisão será agendada automaticamente. Escolha o próximo ciclo:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm">
              <div className="font-medium text-slate-900">{reviewCompleteTask?.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Fonte: <code className="bg-white px-1 rounded">{reviewCompleteTask?.source}</code>
                {reviewCompleteTask?.source_id && ` · ${reviewCompleteTask.source_id}`}
              </div>
            </div>

            {/* Checklist */}
            {getReviewChecklistItems(reviewCompleteTask).length > 0 && (
              <div className="space-y-2">
                <Label>Checklist de revisão (todos obrigatórios)</Label>
                <div className="space-y-1.5 rounded-md border border-slate-200 p-3 bg-white">
                  {getReviewChecklistItems(reviewCompleteTask).map(item => (
                    <label key={item.key} className="flex items-start gap-2 cursor-pointer text-sm hover:bg-slate-50 p-1.5 rounded" data-testid={`review-checklist-${item.key}`}>
                      <input
                        type="checkbox"
                        checked={!!reviewChecklist[item.key]}
                        onChange={(e) => setReviewChecklist(prev => ({ ...prev, [item.key]: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className={reviewChecklist[item.key] ? 'text-slate-500 line-through' : 'text-slate-800'}>
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500">
                  {Object.values(reviewChecklist).filter(Boolean).length}/{getReviewChecklistItems(reviewCompleteTask).length} concluídos
                </div>
              </div>
            )}

            {/* Approver signature */}
            <div className="space-y-2">
              <Label htmlFor="review-approver">Aprovado por *</Label>
              <Input
                id="review-approver"
                value={reviewApprover}
                onChange={(e) => setReviewApprover(e.target.value)}
                placeholder="Ex: João Costa (CISO) / joao@empresa.pt"
                maxLength={200}
                data-testid="review-approver"
              />
              <p className="text-[11px] text-slate-500">
                Nome ou email de quem aprova esta revisão. Ficará registado no histórico da tarefa para trail de auditoria.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Próximo ciclo de revisão</Label>
              <div className="grid grid-cols-4 gap-2">
                {[6, 12, 24, 36].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setReviewCycle(m)}
                    className={cn(
                      'px-3 py-2 rounded-md border text-sm font-medium transition-colors',
                      reviewCycle === m
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
                    )}
                    data-testid={`review-cycle-${m}`}
                  >
                    {m} {m === 12 ? 'mês' : 'meses'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-xs">Personalizado:</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={reviewCycle}
                  onChange={(e) => setReviewCycle(Math.max(1, Math.min(60, parseInt(e.target.value) || 12)))}
                  className="w-24"
                  data-testid="review-cycle-custom"
                />
                <span className="text-xs text-slate-500">meses (1–60)</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-notes">Notas da revisão (opcional)</Label>
              <Textarea
                id="review-notes"
                rows={3}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Ex: Revisão aprovada pelo CISO. Sem alterações materiais."
                maxLength={500}
                data-testid="review-notes"
              />
            </div>
            <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-900">
              ℹ️ A tarefa será marcada como <strong>concluída</strong> e a data da próxima revisão será atualizada para <strong>daqui a {reviewCycle} {reviewCycle === 1 ? 'mês' : 'meses'}</strong>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewCompleteTask(null)} disabled={completingReview}>Cancelar</Button>
            <Button onClick={submitReviewCompletion} disabled={completingReview} data-testid="review-complete-submit">
              {completingReview ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : 'Concluir revisão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}


// ---------- Roadmap Progress Banner ----------
function RoadmapProgressBanner({ data, loading, onClear }) {
  if (!data) return null;
  const overall = data.overall || { total: 0, done: 0, pct: 0, in_progress: 0, blocked: 0, overdue: 0 };
  const weeks = data.weeks || [];

  return (
    <Card className="card-flat overflow-hidden border-indigo-200" data-testid="roadmap-progress-banner">
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4" />
              <span className="text-[10px] font-semibold uppercase tracking-widest opacity-90">
                Plano de Ação · {data.roadmap_id}
              </span>
            </div>
            <h3 className="font-bold text-lg leading-tight mb-1" data-testid="rpb-title">{data.title || 'Plano de Ação 90 dias'}</h3>
            {data.summary && (
              <p className="text-xs text-white/90 line-clamp-2 max-w-3xl">{data.summary.replace(/\*\*/g, '')}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-3xl font-bold tabular-nums" data-testid="rpb-pct">{overall.pct}%</div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">{overall.done}/{overall.total} concluídas</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onClear}
              className="bg-white/15 hover:bg-white/25 text-white border-white/30"
              data-testid="rpb-clear"
            >
              Limpar filtro
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${overall.pct}%` }}
            />
          </div>
        </div>
        <div className="flex gap-4 mt-3 text-xs">
          <Stat label="Em progresso" value={overall.in_progress} />
          <Stat label="Bloqueadas" value={overall.blocked} />
          <Stat label="Em atraso" value={overall.overdue} highlight={overall.overdue > 0} />
        </div>
      </div>

      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="h-4 w-4 text-indigo-600" />
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest">Progresso semanal</p>
          {loading && <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin" />}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {weeks.map((w) => (
            <WeekProgress key={w.week_number} week={w} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn(
        'inline-flex items-center justify-center h-5 min-w-[20px] rounded text-[11px] font-semibold px-1.5 tabular-nums',
        highlight ? 'bg-red-500/90 text-white' : 'bg-white/20 text-white'
      )}>{value}</span>
      <span className="text-white/85">{label}</span>
    </div>
  );
}

function WeekProgress({ week }) {
  const barColor = week.pct >= 100 ? 'bg-emerald-500'
    : week.pct >= 60 ? 'bg-blue-500'
    : week.pct >= 30 ? 'bg-amber-500'
    : week.overdue > 0 ? 'bg-red-500'
    : 'bg-slate-300';
  return (
    <div
      className="border border-slate-200 rounded-lg p-3 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors"
      data-testid={`week-progress-${week.week_number}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded bg-gradient-to-br from-indigo-600 to-purple-600 text-white text-[10px] font-bold">
            W{week.week_number}
          </span>
          <span className="text-xs text-slate-500 tabular-nums">{week.done}/{week.total}</span>
        </div>
        <span className={cn(
          'text-sm font-bold tabular-nums',
          week.pct >= 100 ? 'text-emerald-600'
            : week.pct >= 60 ? 'text-blue-600'
            : week.pct >= 30 ? 'text-amber-600'
            : 'text-slate-500'
        )}>{week.pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${week.pct}%` }} />
      </div>
      {week.focus && <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-1" title={week.focus}>{week.focus}</p>}
      {(week.blocked > 0 || week.overdue > 0) && (
        <div className="flex gap-2 mt-1 text-[10px]">
          {week.blocked > 0 && <span className="text-amber-600">⚠ {week.blocked} bloq.</span>}
          {week.overdue > 0 && <span className="text-red-600">⏰ {week.overdue} atraso</span>}
        </div>
      )}
    </div>
  );
}
